import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Building2, Users, Search, Ban, CheckCircle, Eye, Calendar,
  RefreshCw, MoreHorizontal, Trash2
} from 'lucide-react';

// Helper to get auth config for axios requests (mobile compatibility)
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

export default function SuperAdminTenants() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadTenants();
  }, [user]);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/platform/tenants`, getAuthConfig());
      setTenants(response.data);
    } catch (error) {
      console.error('Failed to load tenants:', error);
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!selectedTenant) return;
    try {
      setProcessing(true);
      await axios.post(`${API}/platform/tenants/${selectedTenant.id}/suspend`, null, {
        ...getAuthConfig(),
        params: { reason: suspendReason || 'Suspended by administrator' }
      });
      toast.success(`Tenant suspended`);
      setShowSuspendDialog(false);
      setSuspendReason('');
      setSelectedTenant(null);
      loadTenants();
    } catch (error) {
      toast.error('Failed to suspend tenant');
    } finally {
      setProcessing(false);
    }
  };

  const handleUnsuspend = async (tenant) => {
    try {
      await axios.post(`${API}/platform/tenants/${tenant.id}/unsuspend`, {}, getAuthConfig());
      toast.success(`Tenant reactivated`);
      loadTenants();
    } catch (error) {
      toast.error('Failed to unsuspend tenant');
    }
  };

  const handleDelete = async () => {
    if (!selectedTenant) return;
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    try {
      setProcessing(true);
      await axios.delete(`${API}/platform/tenants/${selectedTenant.id}`, getAuthConfig());
      toast.success(`Tenant "${selectedTenant.name}" permanently deleted`);
      setShowDeleteDialog(false);
      setDeleteConfirmText('');
      setSelectedTenant(null);
      loadTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tenant');
    } finally {
      setProcessing(false);
    }
  };

  const filteredTenants = tenants.filter(t => 
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.owner?.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const TenantActions = ({ tenant }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white h-8 w-8 p-0">
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800 w-44">
        <DropdownMenuItem className="text-blue-400" onClick={() => navigate(`/super-admin/tenants/${tenant.id}`)}>
          <Eye className="w-4 h-4 mr-2" /> View Details
        </DropdownMenuItem>
        {tenant.status === 'active' ? (
          <DropdownMenuItem className="text-red-400" onClick={() => { setSelectedTenant(tenant); setShowSuspendDialog(true); }}>
            <Ban className="w-4 h-4 mr-2" /> Suspend
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem className="text-emerald-400" onClick={() => handleUnsuspend(tenant)}>
            <CheckCircle className="w-4 h-4 mr-2" /> Reactivate
          </DropdownMenuItem>
        )}
        <DropdownMenuItem className="text-red-500" onClick={() => { setSelectedTenant(tenant); setShowDeleteDialog(true); }}>
          <Trash2 className="w-4 h-4 mr-2" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (!user?.is_super_admin) return null;

  return (
    <SuperAdminLayout onRefresh={loadTenants}>
      <div className="max-w-6xl mx-auto space-y-4">
        {/* Header - Title + Actions on same line */}
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="min-w-0">
            <h1 className="text-lg lg:text-2xl font-bold text-white">Tenants</h1>
            <p className="text-xs lg:text-sm text-zinc-400">
              {tenants.length} total • {tenants.filter(t => t.status === 'active').length} active
            </p>
          </div>
          <Button onClick={loadTenants} variant="outline" size="sm" className="text-zinc-300 flex-shrink-0">
            <RefreshCw className={`w-4 h-4 lg:mr-2 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden lg:inline">Refresh</span>
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Search tenants..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800 text-white text-sm"
          />
        </div>

        <p className="text-xs text-zinc-400">Showing {filteredTenants.length} of {tenants.length} tenants</p>

        {/* Loading/Empty State */}
        {loading ? (
          <div className="text-center py-12 text-zinc-400">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading tenants...
          </div>
        ) : filteredTenants.length === 0 ? (
          <div className="text-center py-12 text-zinc-400">
            <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
            No tenants found
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-zinc-900">
                    <TableHead className="text-zinc-400">Tenant</TableHead>
                    <TableHead className="text-zinc-400">Owner</TableHead>
                    <TableHead className="text-zinc-400">Members</TableHead>
                    <TableHead className="text-zinc-400">Created</TableHead>
                    <TableHead className="text-zinc-400">Status</TableHead>
                    <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTenants.map(tenant => (
                    <TableRow key={tenant.id} className="border-zinc-800 hover:bg-zinc-800">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-zinc-800 rounded-lg flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-blue-400" />
                          </div>
                          <span className="font-medium text-white">{tenant.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-zinc-300">{tenant.owner?.email || '—'}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-zinc-300 flex items-center gap-1">
                          <Users className="w-3 h-3" /> {tenant.member_count}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-zinc-400">
                          {new Date(tenant.created_at).toLocaleDateString()}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={tenant.status === 'active' ? 'bg-emerald-600 text-xs' : 'bg-red-600 text-xs'}>
                          {tenant.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <TenantActions tenant={tenant} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-2">
              {filteredTenants.map(tenant => (
                <div key={tenant.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-white text-sm truncate">{tenant.name}</p>
                          <p className="text-xs text-zinc-400 truncate">{tenant.owner?.email}</p>
                        </div>
                        <TenantActions tenant={tenant} />
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-zinc-500">
                        <span className="flex items-center">
                          <Users className="w-3 h-3 mr-1" /> {tenant.member_count}
                        </span>
                        <span className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {new Date(tenant.created_at).toLocaleDateString()}
                        </span>
                        <Badge className={tenant.status === 'active' ? 'bg-emerald-600 text-xs' : 'bg-red-600 text-xs'}>
                          {tenant.status}
                        </Badge>
                      </div>
                      {tenant.status === 'suspended' && tenant.suspended_reason && (
                        <div className="mt-2 p-2 bg-red-900/30 rounded border border-red-800">
                          <p className="text-xs text-red-300"><strong>Reason:</strong> {tenant.suspended_reason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Suspend Dialog */}
      <Dialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-400" /> Suspend Tenant
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-300 mb-4 text-sm">
              Suspend <strong className="text-white">{selectedTenant?.name}</strong>? All members will lose access.
            </p>
            <Label className="text-zinc-300 text-sm">Reason (optional)</Label>
            <Textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Enter reason..."
              className="mt-2 bg-zinc-800 border-zinc-700 text-white text-sm"
            />
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowSuspendDialog(false)} className="text-zinc-300 w-full sm:w-auto">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleSuspend} disabled={processing} className="w-full sm:w-auto">
              {processing && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              Suspend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => { setShowDeleteDialog(open); if (!open) setDeleteConfirmText(''); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" /> Delete Tenant
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 mb-4">
              <p className="text-red-300 text-sm font-semibold">⚠️ This action is permanent!</p>
              <p className="text-red-300/80 text-xs mt-1">
                All seasons, contestants, scoring data, members, and league data will be permanently deleted.
              </p>
            </div>
            <p className="text-zinc-300 mb-4 text-sm">
              Delete <strong className="text-white">{selectedTenant?.name}</strong>?
            </p>
            <Label className="text-zinc-300 text-sm">Type DELETE to confirm</Label>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              className="mt-2 bg-zinc-800 border-zinc-700 text-white text-sm"
            />
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setShowDeleteDialog(false); setDeleteConfirmText(''); }} className="text-zinc-300 w-full sm:w-auto">
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={processing || deleteConfirmText !== 'DELETE'} 
              className="w-full sm:w-auto"
            >
              {processing && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SuperAdminLayout>
  );
}
